import { useCallback, useMemo } from 'react';
import { isValid } from 'date-fns';

import useToggle from '@lyearn/core/hooks/useToggle';
import { renderErrorMessage } from '@lyearn/form/src/Form/components/InputFields/renderers';
import { ErrorPosition } from '@lyearn/form/src/Form/types';
import { TFunction, useTranslation } from '@lyearn/i18n';
import { Box, DateVariant, NumberInput, Select, Stack, Typography } from '@lyearn/ui';
import DatePicker from '@/components/DatePicker';
import { EndScheduleInfoInput, EndScheduleTypeEnum, Maybe, ScheduleInfo } from '@/types/schema';

import { getEndDateForRecurringEvent, getNewEndScheduleInfo } from '../utils';

const SELECT_INPUT_CLASSES = {
  input: 'truncate py-4 flex-1',
};

interface EndScheduleProps {
  disabled?: boolean;
  onChange: (val: EndScheduleInfoInput) => void;
  error?: JSX.Element;
  startTime: string;
  recurrenceSchedule?: Maybe<ScheduleInfo>;
}

const getEndScheduleTypeOptions = (t: TFunction) => [
  {
    value: EndScheduleTypeEnum.Date,
    name: t('Events:RecurringSession.OnSpecificDate'),
  },
  {
    value: EndScheduleTypeEnum.Occurrences,
    name: t('Events:RecurringSession.AfterSpecificOccurrences'),
  },
];

function EndSchedule({
  disabled,
  onChange,
  error,
  recurrenceSchedule,
  startTime,
}: EndScheduleProps) {
  const { t } = useTranslation();
  const value = recurrenceSchedule?.endScheduleInfo;

  const [showDatePicker, { toggle }] = useToggle(false);

  const maxAllowedEndDate = useMemo(
    () =>
      showDatePicker ? getEndDateForRecurringEvent({ startTime, recurrenceSchedule }) : new Date(),
    [recurrenceSchedule, showDatePicker, startTime],
  );

  const endScheduleTypeOptions = getEndScheduleTypeOptions(t);
  const onChangeEndScheduleType = useCallback(
    (endScheduleType: EndScheduleTypeEnum) => {
      onChange(getNewEndScheduleInfo(endScheduleType));
    },
    [onChange],
  );

  const shouldDisableDate = useCallback(
    (day: Date | null | undefined) => {
      if (!day || !startTime) {
        return true;
      }

      const startDate = new Date(startTime);
      if (day >= startDate && day < maxAllowedEndDate) {
        return false;
      }

      return true;
    },
    [maxAllowedEndDate, startTime],
  );

  return (
    <Box className="space-y-8">
      <Typography className="ms-8" variant="body-short-02">
        {t('Events:RecurringSession.Ends')}
      </Typography>
      <Stack gapX={12}>
        <Select
          disableClearable
          className="flex-1 truncate"
          classes={SELECT_INPUT_CLASSES}
          disabled={disabled}
          getOptionLabel={(option) => option.name}
          options={endScheduleTypeOptions}
          size="large"
          value={value?.endScheduleType}
          onChange={onChangeEndScheduleType}
        />
        {value?.endScheduleType === EndScheduleTypeEnum.Occurrences ? (
          <NumberInput
            className="flex-1"
            disabled={disabled}
            max={60}
            min={1}
            value={value.numberOfOccurrences!}
            onChange={(numberOfOccurrences) => onChange({ ...value, numberOfOccurrences })}
          />
        ) : null}
        {value?.endScheduleType === EndScheduleTypeEnum.Date ? (
          <Box className="flex-1">
            <DatePicker
              className="flex-1"
              disabled={disabled}
              open={showDatePicker}
              pickerProps={{
                variant: DateVariant.Date,
                selectedTime: new Date(value.date!),
                onChange: (date) =>
                  isValid(date) && onChange({ ...value, date: date?.toISOString() }),
              }}
              shouldDisableDate={shouldDisableDate}
              showActionButtons={false}
              onOpenChange={toggle}
            />
          </Box>
        ) : null}
      </Stack>
      {error
        ? renderErrorMessage({ errorMessage: error, errorPosition: ErrorPosition.Custom })
        : null}
    </Box>
  );
}

export default EndSchedule;
